import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./PurchaseOrder.module.css";
import WebService from "../Services/WebService";
import Paginations from "../Customs/pagination/Paginations";
import { FaEye, FaTruck, FaPlus, FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AddPurchaseOrderPanel from "./AddPurchaseOrderPanel";
import { useSelector } from "react-redux";
import { RootState } from "../Services/Store";

interface Order {
  purchase_order_id: number;
  vendor_id: number;
  purchase_date: string;
  purchase_amount: number;
  total_purchase_amount?: number;
  total_quantity?: number;
  payment_due_date: string;
  total_line_number: number;
  status: "PENDING" | "DELIVERED" | "IN TRANSIT" | "IN_PROGRESS" |"COMPLETED";
  delivery_date: string;
  document?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  purchase_order_documents?: any;
}

const ITEMS_PER_PAGE = 15;

// Helper for readable status
function getReadableStatus(status: string): string {
  if (status === "IN TRANSIT") return "In Transit";
  if (status === "PENDING") return "Pending";
  if (status === "DELIVERED") return "Delivered";
  if (status === "IN_PROGRESS") return "In Progress";
  if (status === "IN_PROCESSING") return "In Processing";
  if (status === "COMPLETED") return "Completed";

  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase());
}


const PurchaseOrder: React.FC = () => {
  const navigate = useNavigate();

  // RBAC: Fetch access for purchase_management
  const accessList = useSelector((state: RootState) => state.auth.access);
  const purchaseAccess = accessList.find((a: any) => a.code === "purchase_management");
  const canAddPO = purchaseAccess?.write === true;

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchOrderId, setSearchOrderId] = useState(""); 
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showAddPanel, setShowAddPanel] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // --- Fetch orders using backend filters ---
  const fetchOrders = useCallback(() => {
    setLoading(true);

    // Build query string
    let query = `?limit=${ITEMS_PER_PAGE}&page_number=${page}`;
    if (searchOrderId) query += `&purchase_order_id=${searchOrderId}`;
    if (fromDate) query += `&from_date=${fromDate}`;
    if (toDate) query += `&to_date=${toDate}`;

    WebService.getAPI({
      action: `/v1/door-management/purchase-order${query}`,
    })
      .then((res: any) => {
        const list =
          res?.data?.purchase_orders ||
          res?.data?.orders ||
          [];
        setOrders(list);
        setTotalPages(res?.data?.pagination?.total_pages || 1);
      })
      .catch(() => {
        setOrders([]);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, [page, searchOrderId, fromDate, toDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Debounced PO ID search
  const handleOrderIdSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchOrderId(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
    }, 350);
  };

  // Date filter 
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(e.target.value);
    setPage(1);
  };
  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(e.target.value);
    setPage(1);
  };

  // Refresh orders after new PO created
  const handlePanelClose = () => {
    setShowAddPanel(false);
    setTimeout(fetchOrders, 500);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.header}>
        <div className={styles.controls}>
          <div className={styles.searchInput}>
            <FaSearch className={styles.searchIcon} />
            <input
              placeholder="Search by PO ID..."
              value={searchOrderId}
              onChange={handleOrderIdSearch}
              type="number"
              min={1}
            />
          </div>
          <input
            className={styles.selectInput}
            type="date"
            value={fromDate}
            onChange={handleFromDateChange}
            placeholder="From date"
          />
          <input
            className={styles.selectInput}
            type="date"
            value={toDate}
            onChange={handleToDateChange}
            placeholder="To date"
          />
        </div>
        {/* RBAC: Show Add button only if user has write access */}
        {canAddPO && (
          <button
            type="button"
            className={styles.addButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowAddPanel(true);
            }}
          >
            <FaPlus /> Add Purchase Order
          </button>
        )}
      </div>

      <div className={styles.ordersGrid}>
        {loading ? (
          <div className={styles.loader}>Loading orders...</div>
        ) : orders.length ? (
          orders.map((order) => (
            <div className={styles.card} key={order.purchase_order_id}>
              <div className={styles.cardHeader}>
                <div>
                  <div className={styles.poNumber}>
                    PO-{order.purchase_order_id.toString().padStart(4, "0")}
                  </div>
                  <div className={styles.createdDate}>
                    Created:{" "}
                    {order.created_at
                      ? new Date(order.created_at).toLocaleDateString()
                      : "--"}
                  </div>
                </div>
                <span
                  className={`${styles.statusBadge} ${
                    styles[order.status.replace(" ", "")]
                  }`}
                >
                  {getReadableStatus(order.status)}
                </span>
              </div>
              <div className={styles.cardBody}>
                {/* <div className={styles.vendorName}>
                  {order.vendor_name || "Vendor Name"}
                </div> */}
             {typeof order.purchase_amount === "number" && (
  <div className={styles.amount}>
    Quantity:{" "}
    {order.purchase_amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}
  </div>
)}
{order.total_purchase_amount != null && order.total_purchase_amount !== undefined && (
  <div className={styles.amount}>
    Amount:{" "}
    {`₹${Number(order.total_purchase_amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`}
  </div>
)}

                <div className={styles.deliveryDate}>
                  Delivery:{" "}
                  {order.delivery_date &&
                  order.delivery_date !== "0001-01-01T05:53:28+05:53"
                    ? new Date(order.delivery_date).toLocaleDateString()
                    : "--"}
                </div>
              </div>
              <div className={styles.cardActions}>
                <button
                  className={styles.viewBtn}
                  onClick={() =>
                    navigate(
                      `/purchase-order/${order.purchase_order_id}`
                    )
                  }
                >
                  <FaEye /> View
                </button>
                <button
                  className={styles.trackBtn}
                  onClick={() =>
                    navigate(
                      `/purchase-order/${order.purchase_order_id}`
                    )
                  }
                >
                  <FaTruck /> Track
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.noOrders}>No orders found.</div>
        )}
      </div>

      <Paginations
        totalItems={totalPages * ITEMS_PER_PAGE}
        itemsCountPerPage={ITEMS_PER_PAGE}
        changePage={setPage}
        changeCurrentPage={page}
      />

      {/* === ADD PURCHASE ORDER PANEL === */}
      {showAddPanel && (
        <AddPurchaseOrderPanel open={showAddPanel} onClose={handlePanelClose} />
      )}
    </div>
  );
};

export default PurchaseOrder;